export const ADMIN_IDLE_TIMEOUT_MS = 10 * 60 * 1000;
export const ADMIN_IDLE_WARNING_MS = 60 * 1000;
export const WORKER_IDLE_TIMEOUT_MS = 2 * 60 * 60 * 1000;
export const WORKER_IDLE_WARNING_MS = 5 * 60 * 1000;
export const REMEMBERED_SESSION_DURATION_MS = 30 * 24 * 60 * 60 * 1000;

export const ADMIN_REMEMBERED_EMAIL_KEY = 'bucksAdminRememberedEmail';
export const WORKER_REMEMBERED_EMAIL_KEY = 'bucksWorkerRememberedEmail';
export const PARTNER_REMEMBERED_EMAIL_KEY = 'bucksPartnerRememberedEmail';

export const AUTH_SESSION_STORAGE_KEYS = [
  'adminToken',
  'adminUser',
  'lastActivity',
  'expiresAt',
  'adminRememberMe',
  'rememberedUntil'
];

export const clearAuthSessionStorage = (storage) => {
  try {
    AUTH_SESSION_STORAGE_KEYS.forEach((key) => storage.removeItem(key));
  } catch {
    // Storage can be unavailable in privacy-restricted browser contexts.
  }
};

const ADMIN_SESSION_POLICY = {
  idleTimeoutMs: ADMIN_IDLE_TIMEOUT_MS,
  warningTimeMs: ADMIN_IDLE_WARNING_MS
};

const WORKER_SESSION_POLICY = {
  idleTimeoutMs: WORKER_IDLE_TIMEOUT_MS,
  warningTimeMs: WORKER_IDLE_WARNING_MS
};

const ADMIN_ROLES = new Set(['owner', 'manager', 'admin', 'viewer']);

export const canUseAdminPortal = (user) => ADMIN_ROLES.has(String(user?.role || '').toLowerCase());

export const canUseWorkerPortal = (user) => (
  String(user?.role || '').toLowerCase() === 'worker'
  || (canUseAdminPortal(user) && user?.workerPortalAccess === true)
);

export const canUsePartnerPortal = (user) => (
  String(user?.role || '').toLowerCase() === 'community_partner'
  && Number(user?.partnerOrganizationId) > 0
);

export const getPreferredPortal = (user) => {
  if (user?.portal === 'partner' && canUsePartnerPortal(user)) return 'partner';
  if (user?.portal === 'worker' && canUseWorkerPortal(user)) return 'worker';
  if (user?.portal === 'admin' && canUseAdminPortal(user)) return 'admin';
  if (String(user?.role || '').toLowerCase() === 'worker') return 'worker';
  if (canUsePartnerPortal(user)) return 'partner';
  return 'admin';
};

export const getSessionPolicyForUser = (user) => (
  canUseWorkerPortal(user) && !canUseAdminPortal(user)
    ? WORKER_SESSION_POLICY
    : ADMIN_SESSION_POLICY
);

export const getSessionPolicyForUserRaw = (userRaw) => {
  try {
    return getSessionPolicyForUser(JSON.parse(userRaw || '{}'));
  } catch {
    return ADMIN_SESSION_POLICY;
  }
};

const normalizeRememberedEmail = (email) => String(email || '').trim().toLowerCase();

const readRememberedEmail = (key, storage) => {
  try {
    return normalizeRememberedEmail(storage.getItem(key));
  } catch {
    return '';
  }
};

const updateRememberedEmail = (key, email, shouldRemember, storage) => {
  try {
    if (!shouldRemember) {
      storage.removeItem(key);
      return '';
    }

    const normalizedEmail = normalizeRememberedEmail(email);
    if (normalizedEmail) {
      storage.setItem(key, normalizedEmail);
    } else {
      storage.removeItem(key);
    }
    return normalizedEmail;
  } catch {
    return '';
  }
};

export const readRememberedAdminEmail = (storage = window.localStorage) => (
  readRememberedEmail(ADMIN_REMEMBERED_EMAIL_KEY, storage)
);

export const updateRememberedAdminEmail = (
  email,
  shouldRemember,
  storage = window.localStorage
) => updateRememberedEmail(ADMIN_REMEMBERED_EMAIL_KEY, email, shouldRemember, storage);

export const readRememberedWorkerEmail = (storage = window.localStorage) => (
  readRememberedEmail(WORKER_REMEMBERED_EMAIL_KEY, storage)
);

export const updateRememberedWorkerEmail = (
  email,
  shouldRemember,
  storage = window.localStorage
) => updateRememberedEmail(WORKER_REMEMBERED_EMAIL_KEY, email, shouldRemember, storage);

export const readRememberedPartnerEmail = (storage = window.localStorage) => (
  readRememberedEmail(PARTNER_REMEMBERED_EMAIL_KEY, storage)
);

export const updateRememberedPartnerEmail = (
  email,
  shouldRemember,
  storage = window.localStorage
) => updateRememberedEmail(PARTNER_REMEMBERED_EMAIL_KEY, email, shouldRemember, storage);

export const formatSessionDuration = (durationMs) => {
  const totalMinutes = Math.max(1, Math.round(durationMs / (60 * 1000)));

  if (totalMinutes % 60 === 0) {
    const hours = totalMinutes / 60;
    return `${hours} ${hours === 1 ? 'hour' : 'hours'}`;
  }

  return `${totalMinutes} ${totalMinutes === 1 ? 'minute' : 'minutes'}`;
};
